import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  HostListener,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';
import { Order, OrderStatus } from '../../orders/models/order.models';
import { OrderUtils } from '../../orders/utils/order.utils';
import { CancelOrderModalComponent } from '../cancel-order-modal/cancel-order-modal.component';
import { OrderCancellationSuccessModalComponent } from '../order-cancellation-success-modal/order-cancellation-success-modal.component';
import { CancellationService } from '../../services/cancellation.service';
import { FamilyPlanService, familyPlanError } from '../../services/family-plan.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';
import { ApiService } from '../../services/api.service';
import { LoggerService } from '../../services/logger.service';
import { AuthService } from '../../services/auth.service';
import { RoleService } from '../../services/role.service';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { OrderDetailsStateService } from './services/order-details-state.service';
import { OrderDetailsTapToPayService } from './services/order-details-tap-to-pay.service';
import { OrderSummaryTabComponent } from './components/order-summary-tab/order-summary-tab.component';
import { CustomerInfoTabComponent } from './components/customer-info-tab/customer-info-tab.component';
import { VehicleDetailsTabComponent } from './components/vehicle-details-tab/vehicle-details-tab.component';
import { PaymentTabComponent } from './components/payment-tab/payment-tab.component';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../shared/components/success-modal/success-modal.component';
import {
  buildPaymentSuccessModalData,
  extractPaymentSuccessFieldsFromApiOrder,
  normalizePackageNameForDisplay,
} from '../../shared/utils/payment-success-modal.util';
import { OrdersService } from '../../orders/services/orders.service';
import { getIsMobileOrTablet } from '../../shared/utils/device.util';
import { PaymentMethodCode } from '../../shared/types/payment-method.enum';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { CancelRefundIntent } from '../../shared/components/cancel-refund-panel/cancel-refund-panel.logic';
import { CancelRefundService } from '../../services/cancel-refund.service';
import { OrderRowAction, buildOrderRowActions } from '../../orders/utils/order-row-actions.util';
import { TunnelWashSummaryComponent } from '../../orders/components/tunnel-wash-summary/tunnel-wash-summary.component';

/**
 * Displays a tabbed modal for viewing and managing order details, including summary, booking, vehicle, and payment information.
 * Supports order cancellation, payment tab initialization, tab scrolling, and emits events for modal close, cancellation, booking again, and order updates.
 * @component
 * @selector app-order-details-modal
 * @example
 *   <app-order-details-modal [order]="sample_value1" (closeModal)="handler()"></app-order-details-modal>
 * @input { Order | null } order - The order to display and manage in the modal.
 * @input { boolean } isOpen - Controls whether the modal is open or closed.
 * @output { EventEmitter<void> } closeModal - Emitted when the modal is closed.
 * @output { EventEmitter<Order> } orderCancelled - Emitted when an order is successfully cancelled.
 * @output { EventEmitter<void> } bookAgain - Emitted when the user chooses to book again after cancellation.
 * @output { EventEmitter<void> } orderUpdated - Emitted when the order is updated.
 * @method setActiveTab(tabId: string) - Switches the active tab and triggers any tab-specific initialization.
 * @method onClose() - Closes the modal, resets state, and emits closeModal.
 * @method onCancelOrderConfirm(data) - Confirms cancellation or book-again flow and updates modal state accordingly.
 * @method refreshPaymentTab() - Refreshes the embedded payment tab from external callers.
 * @method showPaymentSuccessModal() - Triggers loading data needed to show the payment success modal.
 * @lifecycle ngOnChanges - Syncs input changes, logs open/close events, fetches order data, and updates payment defaults.
 * @lifecycle ngAfterViewInit - Updates tab scroll state after the view initializes and initializes the payment tab if needed.
 * @lifecycle ngOnDestroy - Cleans up subscriptions and completes the destroy notifier.
 */
@Component({
  selector: 'app-order-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CancelOrderModalComponent,
    OrderCancellationSuccessModalComponent,
    OrderSummaryTabComponent,
    CustomerInfoTabComponent,
    VehicleDetailsTabComponent,
    PaymentTabComponent,
    SuccessModalComponent,
    ConfirmActionModalComponent,
    CancelRefundPanelComponent,
    TunnelWashSummaryComponent,
  ],
  providers: [OrderDetailsStateService, OrderDetailsTapToPayService],
  templateUrl: './order-details-modal.component.html',
  styleUrls: ['./order-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OrderDetailsModalComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() order: Order | null = null;
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() orderCancelled = new EventEmitter<Order>();
  @Output() bookAgain = new EventEmitter<void>();
  @Output() orderUpdated = new EventEmitter<void>();

  @ViewChild('orderTabListContainer') orderTabListContainer?: ElementRef<HTMLDivElement>;
  @ViewChild(PaymentTabComponent) private paymentTabRef?: PaymentTabComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly tapToPaySuccessOrderIds = new Set<string>();

  activeTab = 'order-summary';
  showDropdown = false;
  canScrollLeft = false;
  canScrollRight = true;
  showStopRenewalModal = false;
  isStoppingRenewal = false;

  /** CWS-1862: the shared cancel & refund panel (CWS-1778), opened from the header menu. */
  showCancelRefundPanel = false;
  cancelRefundIntent: CancelRefundIntent = 'cancel';
  isRetryingRefund = false;

  readonly tabs = [
    { id: 'order-summary', label: 'Order Summary', icon: 'clipboard_list' },
    { id: 'customer-info', label: 'Booking Details', icon: 'user' },
    { id: 'vehicle-details', label: 'Vehicle Details', icon: 'car' },
    { id: 'payment', label: 'Payment Details', icon: 'credit_card' },
  ];

  constructor(
    readonly state: OrderDetailsStateService,
    private readonly ttpService: OrderDetailsTapToPayService,
    private readonly ordersService: OrdersService,
    private readonly cancellationService: CancellationService,
    private readonly familyPlanService: FamilyPlanService,
    private readonly apiService: ApiService,
    private readonly messageService: NotificationService,
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly roleService: RoleService,
    private readonly cancelRefundService: CancelRefundService
  ) {
    this.ttpService.paymentSuccess$.pipe(takeUntil(this.destroy$)).subscribe(orderId => {
      this.onTapToPayPaymentSuccess(orderId);
    });

    this.ordersService.paymentSucceeded$.pipe(takeUntil(this.destroy$)).subscribe(({ orderId }) => {
      if (!this.state.showPaymentLinkSentModal || this.state.showPaymentSuccess) {
        return;
      }
      if (!this.isPaymentForCurrentOrder(orderId)) {
        return;
      }
      this.showOrderPaymentSuccessUi(orderId);
      this.afterPaymentSuccessRefresh();
    });
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Handles input changes for the order details modal and tracks open/close events.
   * @example
   * gOnChanges(changes)
   * void
   * @param {SimpleChanges} changes - Angular changes object containing updated input values.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue === true && !changes['isOpen'].previousValue) {
        this.state.modalOpenTime = Date.now();
        this.logger.trackOrder('Order details modal opened', {
          orderId: this.order?.orderId,
          orderStatus: this.order?.status?.value,
          paymentMethod: this.order?.payment_method,
        });
      }
      if (changes['isOpen'].previousValue === true && changes['isOpen'].currentValue === false) {
        this.logger.trackOrder('Order details modal closed', {
          orderId: this.order?.orderId,
          openDurationMs: this.state.modalOpenTime
            ? Date.now() - this.state.modalOpenTime
            : undefined,
        });
        this.resetModalState();
      }
    }

    if (changes['order'] && this.order) {
      this.state.order = this.order;
      if (this.order.id) this.fetchApiOrderData();
      this.updateDefaultPaymentMethod();
      if (this.activeTab === 'payment') {
        this.triggerPaymentTabInitialization();
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateTabScrollState());
    if (this.activeTab === 'payment') {
      this.triggerPaymentTabInitialization();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tab management ─────────────────────────────────────────────────────────

  setActiveTab(tabId: string): void {
    this.activeTab = tabId;
    if (tabId === 'payment') {
      this.triggerPaymentTabInitialization();
    }
    if (tabId === 'vehicle-details') {
      this.vehicleTabRef?.tryLoadFamilyPlanVehicles();
    }
    setTimeout(() => this.ensureActiveTabFullyVisible(), 0);
  }

  onTabScroll(): void {
    this.updateTabScrollState();
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    const delta = container.clientWidth * 0.8;
    container.scrollBy({ left: direction === 'right' ? delta : -delta, behavior: 'smooth' });
    setTimeout(() => this.updateTabScrollState(), 300);
  }

  // ── Modal close ────────────────────────────────────────────────────────────

  onClose(): void {
    this.logger.trackOrder('Order details modal user closed', {
      orderId: this.order?.orderId,
      lastTab: this.activeTab,
      openDurationMs: this.state.modalOpenTime ? Date.now() - this.state.modalOpenTime : undefined,
    });
    this.ttpService.reset();
    this.resetModalState();
    this.closeModal.emit();
  }

  // ── Header helpers ─────────────────────────────────────────────────────────

  getStatusClass(status: OrderStatus): string {
    return `status-${status.color}`;
  }

  /**
   * Legacy cancel (feature off for the shop): today's status rule plus the host's
   * role / ownership rule. With the feature on, the server decides the status
   * side (CWS-1771) and only {@link hostAllowsCancel} applies.
   */
  canCancelOrder(): boolean {
    if (!this.order) return false;
    return OrderUtils.canBeCancelled(this.order) && this.hostAllowsCancel();
  }

  /** Role and ownership only: admins always, shop-floor staff on their own sales. */
  hostAllowsCancel(): boolean {
    if (!this.order) return false;

    if (this.roleService.isAdmin() || this.roleService.isSuperAdmin()) {
      return true;
    }

    if (this.roleService.isShopFloorEmployee()) {
      const currentUser = this.authService.user();
      if (currentUser?.id != null && this.order.userId != null) {
        return String(currentUser.id) === String(this.order.userId);
      }
      return false;
    }

    return true;
  }

  /**
   * CWS-1862: the header menu, built the same way as the Orders list row menu
   * (CWS-1779). With the feature on for the shop the server's `cancellation`
   * flags name the item ("Cancel membership", "Refund single wash") and grey
   * what does not apply; otherwise today's single "Cancel Order" item.
   * "View details" is dropped — the operator is already looking at them.
   */
  get menuActions(): OrderRowAction[] {
    const order = this.order;
    if (!order) return [];
    if (order.cancellation?.available !== true) {
      return this.canCancelOrder() ? [{ key: 'cancel', label: 'Cancel Order', enabled: true }] : [];
    }
    return buildOrderRowActions({
      washTypeValue: order.washTypeValue,
      addOnLabels: order.addOnLabels,
      cancellation: order.cancellation,
      canCancel: this.hostAllowsCancel(),
      alwaysShowCancel: true,
    }).filter(action => action.key !== 'view');
  }

  onMenuAction(action: OrderRowAction): void {
    if (!action.enabled || !this.order) return;
    this.showDropdown = false;
    if (action.key === 'cancel') {
      if (this.order.cancellation?.available === true) {
        this.openCancelRefund('cancel');
      } else {
        this.onCancelOrder();
      }
    } else if (action.key === 'refund') {
      this.openCancelRefund('refund');
    } else if (action.key === 'retry_refund') {
      this.retryRefund();
    }
  }

  trackAction(_index: number, action: OrderRowAction): string {
    return action.key;
  }

  canEditVehicle(): boolean {
    if (!this.order) return false;

    if (this.roleService.isAdmin() || this.roleService.isSuperAdmin()) {
      return true;
    }

    if (this.roleService.isShopFloorEmployee()) {
      const currentUser = this.authService.user();
      if (currentUser?.id != null && this.order.userId != null) {
        return String(currentUser.id) === String(this.order.userId);
      }
      return false;
    }

    return true;
  }

  canShowActions(): boolean {
    return this.menuActions.length > 0 || this.canStopRenewal();
  }

  /**
   * Stop Renewal is offered on family plan membership orders that will still renew (CWS-1231).
   * Company admins only, matching the API.
   */
  canStopRenewal(): boolean {
    const order = this.order;
    if (!order || !this.roleService.isAdmin()) return false;
    if (order.washTypeValue !== 'monthly_membership' || !order.isFamilyPlanPackage) return false;
    if (order.membershipCancelAtPeriodEnd) return false;
    return ['active', 'trialing', 'past_due'].includes(order.membershipStatus ?? '');
  }

  get stopRenewalMessage(): string {
    const end = this.order?.membershipCurrentPeriodEnd;
    const until = end
      ? new Date(end).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;
    return until
      ? `The family plan stays active until ${until}, then won't renew. The customer won't be refunded.`
      : "The family plan stays active until the end of the current billing period, then won't renew. The customer won't be refunded.";
  }

  onStopRenewal(): void {
    if (!this.canStopRenewal()) return;
    this.showDropdown = false;
    this.showStopRenewalModal = true;
  }

  closeStopRenewalModal(): void {
    if (this.isStoppingRenewal) return;
    this.showStopRenewalModal = false;
  }

  onStopRenewalConfirm(): void {
    const order = this.order;
    if (!order?.id || this.isStoppingRenewal) return;
    this.isStoppingRenewal = true;
    this.familyPlanService
      .stopRenewal(order.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.isStoppingRenewal = false;
          this.showStopRenewalModal = false;
          order.membershipCancelAtPeriodEnd = true;
          const until = result?.access_until ?? order.membershipCurrentPeriodEnd;
          if (until) {
            order.membershipCurrentPeriodEnd = until;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Renewal stopped',
            detail: "The family plan won't renew. It stays active until the billing period ends.",
          });
          this.orderUpdated.emit();
        },
        error: (error: unknown) => {
          this.isStoppingRenewal = false;
          this.messageService.add({
            severity: 'error',
            summary: "Couldn't stop renewal",
            detail: familyPlanError(error, 'Something went wrong. Please try again.').message,
          });
        },
      });
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showDropdown = false;
  }

  // ── Cancel order flow ──────────────────────────────────────────────────────

  onCancelOrder(): void {
    if (!this.order || !this.canCancelOrder()) return;
    this.showDropdown = false;
    this.state.showCancelOrderModal = true;
  }

  closeCancelOrderModal(): void {
    this.state.showCancelOrderModal = false;
  }

  /**
   * Cancels an order or closes the modal when the user chooses to book again.
   * @example
   * nCancelOrderConfirm({ order: someOrder, reason: 'Changed plans' })
   * void
   * @param {{ order: Order; reason: string; action?: string }} data - Cancellation details including the order, reason, and optional action.
   * @returns {void} Nothing.
   **/
  onCancelOrderConfirm(data: { order: Order; reason: string; action?: string }): void {
    if (data.action === 'book-again') {
      this.onClose();
    } else {
      this.cancellationService.cancelOrder(data.order.id, data.reason).subscribe({
        next: response => {
          if (response.status) {
            const wasScheduledOrder = this.order?.status?.value === 'scheduled';
            const orderPrice = this.order?.price || '';
            if (this.order) {
              this.order.status = { label: 'Canceled', value: 'canceled', color: 'red' };
            }
            this.state.cancellationWasScheduled = wasScheduledOrder;
            this.state.cancellationRefundAmount = orderPrice;
            // Show success modal BEFORE emitting orderCancelled: the parent closes the overlay
            // on orderCancelled which triggers state.reset(), which would hide the success modal
            // if we set it after the emit. Setting it first ensures it persists post-reset.
            this.state.showCancellationSuccessModal = true;
            if (this.order) {
              this.orderCancelled.emit(this.order);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to cancel order. Please try again.',
            });
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to cancel order. Please try again.',
          });
        },
      });
    }
    this.closeCancelOrderModal();
  }

  onCancellationSuccessClose(): void {
    this.state.showCancellationSuccessModal = false;
    this.state.cancellationRefundAmount = '';
    this.state.cancellationWasScheduled = false;
  }

  onCancellationBookAgain(): void {
    this.state.showCancellationSuccessModal = false;
    this.state.cancellationRefundAmount = '';
    this.state.cancellationWasScheduled = false;
    this.onClose();
    this.bookAgain.emit();
  }

  // ── Cancel & refund panel (CWS-1862) ───────────────────────────────────────

  private openCancelRefund(intent: CancelRefundIntent): void {
    this.cancelRefundIntent = intent;
    this.showCancelRefundPanel = true;
  }

  closeCancelRefundPanel(): void {
    this.showCancelRefundPanel = false;
  }

  /**
   * The panel executed a cancellation or refund. As on the Orders list, the
   * operator reads what happened in a toast; the order's status and flags moved,
   * so the host is told through `orderCancelled` — every host reloads its list
   * and closes this modal on it.
   */
  onCancelRefundDone(done?: CancelRefundDone): void {
    this.closeCancelRefundPanel();
    if (done) {
      const failed = done.result.status === 'refund_failed' || done.result.status === 'failed';
      const refundOnly = done.result.cancellation?.action === 'refund';
      let summary = 'Cancelled';
      if (failed) summary = 'Refund not sent';
      else if (refundOnly) summary = 'Refunded';
      this.messageService.add({
        severity: failed ? 'warn' : 'success',
        summary,
        detail: done.summary,
      });
    }
    if (this.order) {
      this.orderCancelled.emit(this.order);
    }
  }

  /** CWS-1779: the order's last cancellation still owes a refund; retry it in place. */
  retryRefund(): void {
    const order = this.order;
    const cancellationId = order?.cancellation?.refund_failed?.cancellation_id;
    if (!order || !cancellationId || this.isRetryingRefund) return;
    this.isRetryingRefund = true;
    this.cancelRefundService
      .retryRefund(cancellationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isRetryingRefund = false;
        })
      )
      .subscribe({
        next: res => {
          if (res.status && res.data && res.data.status !== 'refund_failed') {
            this.messageService.add({
              severity: 'success',
              summary: 'Refund sent',
              detail: `The refund on ${order.orderId} has gone out.`,
            });
            this.onCancelRefundDone();
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail:
              res.message || `The refund on ${order.orderId} could not be sent. Try again later.`,
          });
        },
        error: (err: unknown) => {
          const message = (err as { error?: { message?: string } })?.error?.message;
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail: message || `The refund on ${order.orderId} could not be sent.`,
          });
        },
      });
  }

  // ── Public API (callable from parent via @ViewChild) ───────────────────────

  refreshPaymentTab(): void {
    this.paymentTabRef?.refreshPaymentTab();
  }

  /** Payment link Pusher path only; tap-to-pay uses Android bridge → paymentSuccess$. */
  showPaymentSuccessModal(): void {
    if (this.state.showPaymentSuccess) {
      return;
    }
    this.showOrderPaymentSuccessUi(this.order?.id);
    this.afterPaymentSuccessRefresh();
  }

  onPaymentCompleted(): void {
    if (this.state.showPaymentSuccess) {
      return;
    }
    this.showOrderPaymentSuccessUi(this.order?.id);
    this.afterPaymentSuccessRefresh();
  }

  onPaymentSuccessModalClose(): void {
    this.onClose();
    this.state.showPaymentSuccess = false;
  }

  onPaymentSuccessAction(): void {
    this.onClose();
    this.state.showPaymentSuccess = false;
  }

  private onTapToPayPaymentSuccess(orderId: string): void {
    const normalized = String(orderId ?? '')
      .replace(/^#+/, '')
      .trim();
    if (!normalized || this.state.showPaymentSuccess) {
      return;
    }
    if (this.tapToPaySuccessOrderIds.has(normalized)) {
      return;
    }
    this.tapToPaySuccessOrderIds.add(normalized);
    setTimeout(() => this.tapToPaySuccessOrderIds.delete(normalized), 5 * 60 * 1000);
    this.showOrderPaymentSuccessUi(normalized);
    this.afterPaymentSuccessRefresh();
  }

  /** Same pattern as notification modal: snapshot from order, then merge API details. */
  private showOrderPaymentSuccessUi(orderId?: string): void {
    const id = String(orderId ?? this.order?.id ?? '').trim();
    if (!id || this.state.showPaymentSuccess) {
      return;
    }
    this.state.showPaymentLinkSentModal = false;
    this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(null), false);
    this.fetchOrderDetailsForSuccessModal(id);
  }

  private fetchOrderDetailsForSuccessModal(orderId: string): void {
    if (!orderId) {
      return;
    }
    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = this.extractOrderRecordFromApiResponse(orderResponse);
          this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(apiOrder), true);
        },
        error: () => {
          if (!this.state.showPaymentSuccess) {
            this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(null), false);
          }
        },
      });
  }

  private applySuccessModalSnapshot(snapshot: SuccessModalData, merge: boolean): void {
    const prev = this.state.successModalData;
    const keep = (next: string, old: string) => {
      const n = (next || '').trim();
      const o = (old || '').trim();
      if (merge && !n && o) {
        return o;
      }
      return n || o;
    };
    this.state.successModalData = {
      title: snapshot.title || prev.title,
      description: snapshot.description || prev.description,
      buttonText: snapshot.buttonText || prev.buttonText,
      packageName: keep(snapshot.packageName ?? '', prev.packageName ?? ''),
      customerName: keep(snapshot.customerName ?? '', prev.customerName ?? ''),
      vehicleYear: keep(snapshot.vehicleYear ?? '', prev.vehicleYear ?? ''),
      vehicleMake: keep(snapshot.vehicleMake ?? '', prev.vehicleMake ?? ''),
      vehicleModel: keep(snapshot.vehicleModel ?? '', prev.vehicleModel ?? ''),
    };
    this.state.showPaymentSuccess = true;
  }

  private buildSuccessModalSnapshot(apiOrder?: Record<string, any> | null): SuccessModalData {
    const fallbackPackage = normalizePackageNameForDisplay(
      this.order?.packageName || this.order?.washType || ''
    );
    const vehicle = this.parseVehicleInfoFromOrder(this.order?.vehicleInfo || '');
    const fields = apiOrder
      ? extractPaymentSuccessFieldsFromApiOrder(apiOrder, {
          packageName: fallbackPackage,
          customerName: this.order?.customerName || 'Customer',
          vehicleYear: vehicle.year,
          vehicleMake: vehicle.make,
          vehicleModel: vehicle.model,
        })
      : {
          packageName: fallbackPackage,
          customerName: this.order?.customerName || 'Customer',
          vehicleYear: vehicle.year,
          vehicleMake: vehicle.make,
          vehicleModel: vehicle.model,
        };
    if (!fields.packageName) {
      fields.packageName = fallbackPackage;
    }
    return buildPaymentSuccessModalData(fields);
  }

  private extractOrderRecordFromApiResponse(response: any): Record<string, any> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const r = response as Record<string, any>;
    const data = r['data'];
    if (data && typeof data === 'object') {
      const d = data as Record<string, any>;
      const nested = d['order'];
      if (nested && typeof nested === 'object') {
        return nested as Record<string, any>;
      }
      return d;
    }
    return r;
  }

  private parseVehicleInfoFromOrder(vehicleInfo: string): {
    year: string;
    make: string;
    model: string;
  } {
    if (!vehicleInfo?.trim()) {
      return { year: '', make: '', model: '' };
    }
    const plateMatch = vehicleInfo.match(/\(([^)]+)\)\s*$/);
    const withoutPlate = plateMatch
      ? vehicleInfo.slice(0, plateMatch.index).trim()
      : vehicleInfo.trim();
    const parts = withoutPlate.split(/\s+/);
    if (parts.length === 0) {
      return { year: '', make: '', model: '' };
    }
    const year = /^\d{4}$/.test(parts[0]) ? parts.shift()! : '';
    const make = parts.shift() || '';
    const model = parts.join(' ');
    return { year, make, model };
  }

  private isPaymentForCurrentOrder(orderId: string): boolean {
    const paid = String(orderId ?? '').trim();
    if (!paid || !this.order) {
      return false;
    }
    const internalId = String(this.order.id ?? '').trim();
    const publicId = String(this.order.orderId ?? '').trim();
    return paid === internalId || paid === publicId;
  }

  private afterPaymentSuccessRefresh(): void {
    setTimeout(() => {
      this.paymentTabRef?.refreshPaymentTab();
      this.orderUpdated.emit();
    }, 1000);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private get vehicleTabRef(): VehicleDetailsTabComponent | undefined {
    return undefined; // Resolved at runtime via tab component itself
  }

  /**
   * Resets the order details modal state to its default values and scroll position.
   * @example
   * resetModalState()
   * void
   * @returns {void} No return value.
   **/
  private resetModalState(): void {
    this.activeTab = 'order-summary';
    this.showDropdown = false;
    this.showStopRenewalModal = false;
    this.isStoppingRenewal = false;
    this.showCancelRefundPanel = false;
    this.cancelRefundIntent = 'cancel';
    this.canScrollLeft = false;
    this.canScrollRight = true;
    this.tapToPaySuccessOrderIds.clear();
    this.state.reset();
    setTimeout(() => {
      if (this.orderTabListContainer?.nativeElement) {
        this.orderTabListContainer.nativeElement.scrollLeft = 0;
      }
    }, 0);
  }

  /**
   * Fetches order data from the API for the current order and stores it in component state.
   * @example
   * fetchApiOrderData()
   * void
   * @returns {void} No return value.
   **/
  private fetchApiOrderData(): void {
    if (!this.order?.id) return;
    this.apiService
      .get(`order/${this.order.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const { data } = orderResponse ?? {};
          const raw = data?.order ?? data ?? orderResponse;
          this.state.apiOrderData = raw ?? null;
          if (this.order && raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const r = raw as Record<string, any>;
            const uid = r['user_id'];
            const uname = r['user_name'];
            if (uid != null && uid !== '' && !this.order.userId) {
              this.order.userId = String(uid);
            }
            if (uname != null && uname !== '' && !this.order.userName) {
              this.order.userName = String(uname);
            }
            this.state.order = this.order;
          }
        },
        error: () => {
          this.state.apiOrderData = null;
        },
      });
  }

  /**
   * Updates the default payment method based on order type and device type.
   * @example
   * updateDefaultPaymentMethod()
   * void
   * @returns {void} This method does not return a value.
   **/
  private updateDefaultPaymentMethod(): void {
    if (!this.order) return;
    const isMobile = getIsMobileOrTablet();
    const isSubscription = this.isCashPaymentDisabled();
    if (isSubscription && !isMobile) {
      this.state.paymentForm.method = PaymentMethodCode.Card;
    } else if (this.state.paymentForm.method === PaymentMethodCode.Cash && isSubscription) {
      this.state.paymentForm.method = isMobile
        ? PaymentMethodCode.TapToPay
        : PaymentMethodCode.Card;
    } else if (
      !this.state.paymentForm.method ||
      this.state.paymentForm.method === PaymentMethodCode.Cash
    ) {
      this.state.paymentForm.method = isMobile
        ? PaymentMethodCode.TapToPay
        : PaymentMethodCode.Cash;
    }
  }

  private isCashPaymentDisabled(): boolean {
    if (!this.order) return false;
    const washType = this.order.washTypeValue || '';
    if (washType === 'monthly_membership' || washType === 'monthly') return true;
    const washTypeStr = this.order.washType || '';
    return (
      washTypeStr.toLowerCase().includes('monthly') ||
      washTypeStr.toLowerCase().includes('membership')
    );
  }

  private updateTabScrollState(): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight =
      Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth;
  }

  /**
   * Ensures the active tab is fully visible within the tab container by scrolling it into view when needed.
   * @example
   * ensureActiveTabFullyVisible()
   * undefined
   * @returns {void} No return value.
   **/
  private ensureActiveTabFullyVisible(): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    const activeEl = container.querySelector('.tab-item.active') as HTMLElement | null;
    if (!activeEl) return;
    const padding = 60;
    const elLeft = activeEl.offsetLeft - padding;
    const elRight = activeEl.offsetLeft + activeEl.offsetWidth + padding;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    if (elLeft < viewLeft) {
      container.scrollTo({ left: elLeft, behavior: 'smooth' });
    } else if (elRight > viewRight) {
      container.scrollTo({ left: elRight - container.clientWidth, behavior: 'smooth' });
    }
    this.updateTabScrollState();
  }

  private triggerPaymentTabInitialization(retries = 3): void {
    setTimeout(() => {
      if (this.paymentTabRef) {
        this.paymentTabRef.initializeOnTabOpen();
        return;
      }
      if (retries > 0) {
        this.triggerPaymentTabInitialization(retries - 1);
      }
    }, 0);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
